import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Globe, Database, Search, ChevronDown, Check, X, Users, Building, MapPin, Mail, ChevronLeft, ChevronRight, LogOut
} from 'lucide-react';

export const LinkedInTargetingSetup = ({ onBack }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [jobTitles, setJobTitles] = useState('');
  const [targetCompanies, setTargetCompanies] = useState('');
  const [location, setLocation] = useState('San Francisco, New York, Remote, Los Angeles, Seattle');
  const [keywords, setKeywords] = useState('');
  const [selectedCompanySizes, setSelectedCompanySizes] = useState(['All Sizes']);
  const [selectedIndustries, setSelectedIndustries] = useState(['All Industries']);
  const [isIndustryDropdownOpen, setIsIndustryDropdownOpen] = useState(false);
  const [industrySearchTerm, setIndustrySearchTerm] = useState('');
  const [isCompanySizeDropdownOpen, setIsCompanySizeDropdownOpen] = useState(false);
  const [companySizeSearchTerm, setCompanySizeSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Step 1 & 2 process states
  const [searchResults, setSearchResults] = useState(null);

  // Step 2 validation states
  const [validationResults, setValidationResults] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [estimatedContacts, setEstimatedContacts] = useState(0);
  const [estimatedPages, setEstimatedPages] = useState(0);

  // Step 3 results states
  const [resultsCurrentPage, setResultsCurrentPage] = useState(1);
  const [resultsPerPage] = useState(20);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectAllEmails, setSelectAllEmails] = useState(false);
  const [selectFirst50, setSelectFirst50] = useState(false);

  // Page fetching progress states
  const [isFetchingAllPages, setIsFetchingAllPages] = useState(false);
  const [fetchProgress, setFetchProgress] = useState({ current: 0, total: 0 });

  // Saving leads states
  const [isSavingLeads, setIsSavingLeads] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Bulk match loading state
  const [isBulkMatching, setIsBulkMatching] = useState(false);
  const [isFindingSelectedEmails, setIsFindingSelectedEmails] = useState(false);
  const [emailLookupResult, setEmailLookupResult] = useState(null);

  // Cancellation ref (for immediate synchronous updates)
  const isCancelledRef = useRef(false);

  // Page fetching states
  const [currentPageData, setCurrentPageData] = useState([]);
  const [totalCreditsUsed, setTotalCreditsUsed] = useState(0);
  const [isFetchingPage, setIsFetchingPage] = useState(false);
  const [originalSearchParams, setOriginalSearchParams] = useState(null);

  // Credit limit state
  const [creditLimit, setCreditLimit] = useState('');

  // Email option selection state
  const [selectedEmailOption, setSelectedEmailOption] = useState('allEmails');

  // Accept guess option state
  const [acceptGuess, setAcceptGuess] = useState(false);

  // Track if all pages have been fetched (to avoid re-fetching when returning from Step 3)
  const [allPagesFetched, setAllPagesFetched] = useState(false);





  // Autocomplete state for target companies
  const [suggestions, setSuggestions] = useState([]);
  const [companyCache, setCompanyCache] = useState({});
  const containerRef = useRef(null);
  const industryDropdownRef = useRef(null);
  const companySizeDropdownRef = useRef(null);

  function normalizeDomain(input) {
    return input
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0];
  }

  function isLikelyCompanyDomain(input) {
    if (!input || typeof input !== 'string') return false;

    let value = input.trim().toLowerCase();

    // Strip protocol
    value = value.replace(/^https?:\/\//, '');
    value = value.replace(/^www\./, '');

    // Reject obvious non-domains
    const blocked = [
      'linkedin.com',
      'facebook.com',
      'instagram.com',
      'twitter.com',
      'x.com',
      'youtube.com'
    ];

    if (blocked.some(d => value === d || value.startsWith(d + '/'))) {
      return false;
    }

    // Reject emails
    if (value.includes('@')) return false;

    // Reject IP addresses
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(value)) return false;

    // Must contain at least one dot
    if (!value.includes('.')) return false;

    // Basic domain pattern
    const domainRegex =
      /^(?:[a-z0-9-]+\.)+[a-z]{2,}$/;

    return domainRegex.test(value.split('/')[0]);
  }
  // Reusable function to resolve company name with Clearbit + Clearout fallback
  const resolveCompanyWithFallback = async (companyName) => {
    if (isLikelyCompanyDomain(companyName)) {
      const domain = normalizeDomain(companyName);
      console.log(`Input "${companyName}" looks like a domain. Using as-is: ${domain}`);
      return {
        name: companyName,
        domain: domain,
        originalName: companyName,
        source: 'user'
      };
    }
    // ---------------------------
    // 1️⃣ Try Clearbit (primary)
    // ---------------------------
    try {
      const res = await axios.get('https://autocomplete.clearbit.com/v1/companies/suggest', {
        params: { query: companyName }
      });

      const data = res.data;

      if (data && data.length > 0) {
        // Exact match
        const exactMatch = data.find(c =>
          c.name?.toLowerCase() === companyName.toLowerCase()
        );
        if (exactMatch) {
          return {
            name: companyName,
            domain: exactMatch.domain,
            originalName: exactMatch.name,
            source: "clearbit"
          };
        }

        // Best match (Clearbit)
        const bestMatch = data[0];
        if (bestMatch) {
          return {
            name: companyName,
            domain: bestMatch.domain,
            originalName: bestMatch.name,
            source: "clearbit"
          };
        }
      }

    } catch (err) {
      console.warn(`Clearbit failed for ${companyName}:`, err.message);
      // Will fall through to Clearout
    }

    // ---------------------------
    // 2️⃣ Fallback: Clearout
    // ---------------------------
    try {
      const res = await axios.get('https://api.clearout.io/public/companies/autocomplete', {
        params: { query: companyName },
        headers: {
          "Authorization": undefined, "Accept": "application/json"
        }
      });

      const data = res.data?.data;

      if (data && data.length > 0) {
        // Exact match
        const exactMatch = data.find(c =>
          c.name?.toLowerCase() === companyName.toLowerCase()
        );
        if (exactMatch) {
          return {
            name: companyName,
            domain: exactMatch.domain,
            originalName: exactMatch.name,
            source: "clearout"
          };
        }

        // Best match (Clearout)
        const bestMatch = data[0];
        if (bestMatch) {
          return {
            name: companyName,
            domain: bestMatch.domain,
            originalName: bestMatch.name,
            source: "clearout"
          };
        }
      }

    } catch (err) {
      console.warn(`Clearout failed for ${companyName}:`, err.message);
    }

    // ---------------------------
    // 3️⃣ Last fallback: Google Search via server
    // ---------------------------
    try {
      const response = await axios.post('/api/apollo/google-company-search', {
        companyName
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success && response.data.result) {
        console.log(`Google fallback found domain for ${companyName}:`, response.data.result.domain);
        return response.data.result;
      }
    } catch (err) {
      console.warn(`Google fallback failed for ${companyName}:`, err.message);
    }

    // ---------------------------
    // 4️⃣ Final failure
    // ---------------------------
    return null;
  };

  // Fetch suggestions when user types after last comma
  useEffect(() => {
    const parts = targetCompanies.split(',').map(p => p.trim());
    const current = parts[parts.length - 1];
    if (!current || current.length < 2) {
      setSuggestions([]);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        const result = await resolveCompanyWithFallback(current);
        if (result) {
          // Convert the result back to the format expected by the autocomplete
          setSuggestions([{
            name: result.originalName,
            domain: result.domain,
            logo: null // We don't get logos from the fallback function
          }]);
        } else {
          setSuggestions([]);
        }
      } catch (err) {
        console.error('Company autocomplete error:', err);
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [targetCompanies]);

  // Click outside to close suggestions
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSuggestions([]);
      }
      if (industryDropdownRef.current && !industryDropdownRef.current.contains(e.target)) {
        setIsIndustryDropdownOpen(false);
      }
      if (companySizeDropdownRef.current && !companySizeDropdownRef.current.contains(e.target)) {
        setIsCompanySizeDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Handle suggestion selection
  const handleCompanySelect = (company) => {
    const parts = targetCompanies.split(',');
    parts[parts.length - 1] = ` ${company.name}`;
    setTargetCompanies(parts.join(',').trim() + ', ');
    setCompanyCache(prev => ({ ...prev, [company.name]: company.domain }));
    setSuggestions([]);
  };

  // Handle industry selection/deselection
  const handleIndustryToggle = (industry) => {
    if (industry === 'All Industries') {
      setSelectedIndustries(['All Industries']);
    } else {
      setSelectedIndustries(prev => {
        const filtered = prev.filter(i => i !== 'All Industries');
        if (filtered.includes(industry)) {
          const newSelection = filtered.filter(i => i !== industry);
          return newSelection.length === 0 ? ['All Industries'] : newSelection;
        } else {
          return [...filtered, industry];
        }
      });
    }
  };

  // Get display text for selected industries
  const getSelectedIndustriesDisplay = () => {
    if (selectedIndustries.length === 0 || (selectedIndustries.length === 1 && selectedIndustries[0] === 'All Industries')) {
      return 'All Industries';
    }
    if (selectedIndustries.length === 1) {
      return selectedIndustries[0];
    }
    return `${selectedIndustries.length} Industries Selected`;
  };

  // Handle company size selection/deselection
  const handleCompanySizeToggle = (size) => {
    if (size === 'All Sizes') {
      setSelectedCompanySizes(['All Sizes']);
    } else {
      setSelectedCompanySizes(prev => {
        const filtered = prev.filter(s => s !== 'All Sizes');
        if (filtered.includes(size)) {
          const newSelection = filtered.filter(s => s !== size);
          return newSelection.length === 0 ? ['All Sizes'] : newSelection;
        } else {
          return [...filtered, size];
        }
      });
    }
  };

  // Get display text for selected company sizes
  const getSelectedCompanySizesDisplay = () => {
    if (selectedCompanySizes.length === 0 || (selectedCompanySizes.length === 1 && selectedCompanySizes[0] === 'All Sizes')) {
      return 'All Sizes';
    }
    if (selectedCompanySizes.length === 1) {
      return selectedCompanySizes[0];
    }
    return `${selectedCompanySizes.length} Sizes Selected`;
  };


  // Filter industries based on search term
  const getFilteredIndustries = () => {
    if (!industrySearchTerm.trim()) {
      return industries;
    }
    return industries.filter(industry =>
      industry.toLowerCase().includes(industrySearchTerm.toLowerCase())
    );
  };

  // Filter company sizes based on search term
  const getFilteredCompanySizes = () => {
    if (!companySizeSearchTerm.trim()) {
      return companySizes;
    }
    return companySizes.filter(size =>
      size.toLowerCase().includes(companySizeSearchTerm.toLowerCase())
    );
  };

  // Helper function to get company domains for API submission
  const getCompanyDomainsForSubmission = () => {
    const companies = targetCompanies.split(',').map(company => company.trim()).filter(company => company);
    return companies.map(companyName => {
      // Return domain if we have it cached, otherwise return the company name
      return companyCache[companyName] || companyName;
    }).filter(item => item);
  };

  // Helper function to map company sizes to Apollo format
  const getCompanySizeRanges = () => {
    if (selectedCompanySizes.length === 0 || (selectedCompanySizes.length === 1 && selectedCompanySizes[0] === 'All Sizes')) {
      return ['All Sizes'];
    }

    const sizeMap = {
      '1-10 employees': '1,10',
      '11-20 employees': '11,20',
      '21-50 employees': '21,50',
      '51-100 employees': '51,100',
      '101-200 employees': '101,200',
      '201-500 employees': '201,500',
      '501-1000 employees': '501,1000',
      '1001-2000 employees': '1001,2000',
      '2001-5000 employees': '2001,5000',
      '5001-10000 employees': '5001,10000',
      '10001+ employees': '10001'
    };

    return selectedCompanySizes
      .filter(size => size !== 'All Sizes')
      .map(size => sizeMap[size])
      .filter(range => range);
  };

  // Function to try to resolve domains for manually typed companies
  const tryResolveMissingDomains = async () => {
    const companies = targetCompanies.split(',').map(company => company.trim()).filter(company => company);
    const uncachedCompanies = companies.filter(company => !companyCache[company]);

    if (uncachedCompanies.length === 0) return;

    // Try to resolve domains for companies not in cache
    const resolvePromises = uncachedCompanies.map(async (companyName) => {
      return await resolveCompanyWithFallback(companyName);
    });

    const resolved = await Promise.all(resolvePromises);
    const newCache = {};
    resolved.forEach(result => {
      if (result) {
        newCache[result.name] = result.domain;
      }
    });

    if (Object.keys(newCache).length > 0) {
      // Update companyCache directly to avoid async state race condition
      Object.assign(companyCache, newCache);
      console.log("Resolved company domains:", newCache);
      console.log("companyCache after update:", companyCache);
    }
    return resolved.filter(r => r !== null);
  };

  const companySizes = [
    'All Sizes',
    '1-10 employees',
    '11-20 employees',
    '21-50 employees',
    '51-100 employees',
    '101-200 employees',
    '201-500 employees',
    '501-1000 employees',
    '1001-2000 employees',
    '2001-5000 employees',
    '5001-10000 employees',
    '10001+ employees'
  ];

  const industries = [
    'All Industries',
    'Accounting',
    'Agriculture',
    'Airlines/Aviation',
    'Alternative Dispute Resolution',
    'Alternative Medicine',
    'Animation',
    'Apparel & Fashion',
    'Architecture & Planning',
    'Arts & Crafts',
    'Automotive',
    'Aviation & Aerospace',
    'Banking',
    'Biotechnology',
    'Broadcast Media',
    'Building Materials',
    'Business Supplies & Equipment',
    'Capital Markets',
    'Chemicals',
    'Civic & Social Organization',
    'Civil Engineering',
    'Commercial Real Estate',
    'Computer & Network Security',
    'Computer Games',
    'Computer Hardware',
    'Computer Networking',
    'Computer Software',
    'Construction',
    'Consumer Electronics',
    'Consumer Goods',
    'Consumer Services',
    'Cosmetics',
    'Dairy',
    'Defense & Space',
    'Design',
    'E-Learning',
    'Education Management',
    'Electrical/Electronic Manufacturing',
    'Entertainment',
    'Environmental Services',
    'Events Services',
    'Executive Office',
    'Facilities Services',
    'Farming',
    'Financial Services',
    'Fine Art',
    'Fishery',
    'Food & Beverages',
    'Food Production',
    'Fund-Raising',
    'Furniture',
    'Gambling & Casinos',
    'Glass, Ceramics & Concrete',
    'Government Administration',
    'Government Relations',
    'Graphic Design',
    'Health, Wellness & Fitness',
    'Higher Education',
    'Hospital & Health Care',
    'Hospitality',
    'Human Resources',
    'Import & Export',
    'Individual & Family Services',
    'Industrial Automation',
    'Information Services',
    'Information Technology & Services',
    'Insurance',
    'International Affairs',
    'International Trade & Development',
    'Internet',
    'Investment Banking',
    'Investment Management',
    'Judiciary',
    'Law Enforcement',
    'Law Practice',
    'Legal Services',
    'Legislative Office',
    'Leisure, Travel & Tourism',
    'Libraries',
    'Logistics & Supply Chain',
    'Luxury Goods & Jewelry',
    'Machinery',
    'Management Consulting',
    'Maritime',
    'Market Research',
    'Marketing & Advertising',
    'Mechanical or Industrial Engineering',
    'Media Production',
    'Medical Devices',
    'Medical Practice',
    'Mental Health Care',
    'Military',
    'Mining & Metals',
    'Motion Pictures & Film',
    'Museums & Institutions',
    'Music',
    'Nanotechnology',
    'Newspapers',
    'Nonprofit Organization Management',
    'Oil & Energy',
    'Online Media',
    'Outsourcing/Offshoring',
    'Package/Freight Delivery',
    'Packaging & Containers',
    'Paper & Forest Products',
    'Performing Arts',
    'Pharmaceuticals',
    'Philanthropy',
    'Photography',
    'Plastics',
    'Political Organization',
    'Primary/Secondary Education',
    'Professional Training & Coaching',
    'Program Development',
    'Public Policy',
    'Public Relations & Communications',
    'Public Safety',
    'Publishing',
    'Railroad Manufacture',
    'Ranching',
    'Real Estate',
    'Recreational Facilities & Services',
    'Religious Institutions',
    'Renewables & Environment',
    'Research',
    'Restaurants',
    'Retail',
    'Security & Investigations',
    'Semiconductors',
    'Shipbuilding',
    'Sporting Goods',
    'Sports',
    'Staffing & Recruiting',
    'Supermarkets',
    'Telecommunications',
    'Textiles',
    'Think Tanks',
    'Tobacco',
    'Translation & Localization',
    'Transportation/Trucking/Railroad',
    'Utilities',
    'Venture Capital & Private Equity',
    'Veterinary',
    'Warehousing',
    'Wholesale',
    'Wine & Spirits',
    'Wireless',
    'Writing & Editing'
  ];

  const handleStartScraping = async () => {
    setIsLoading(true);
    isCancelledRef.current = false; // Reset cancellation flag

    try {
      console.log("currentStep:", currentStep);
      // If called from Step 2, we already have the first page from validation
      if (currentStep === 2 && validationResults) {
        // Prepare search params
        const scrapingParams = {
          jobTitles: jobTitles.split(',').map(title => title.trim()).filter(title => title),
          targetCompanies: getCompanyDomainsForSubmission(),
          location: location.split(',').map(loc => loc.trim()).filter(loc => loc),
          keywords: keywords.split(',').map(keyword => keyword.trim()).filter(keyword => keyword),
          companySizes: getCompanySizeRanges(),
          industries: selectedIndustries.length === 0 || (selectedIndustries.length === 1 && selectedIndustries[0] === 'All Industries') ? ['All Industries'] : selectedIndustries
        };

        // Use existing validation results as the first page
        const firstPageResult = {
          allPeople: validationResults.people || validationResults.allPeople || [],
          jobId: validationResults.jobId,
          currentPage: 1,
          totalPages: validationResults.totalPages || estimatedPages,
          creditsUsed: 0, // Apollo People API Search does not use credits
          additionalPagesAvailable: (validationResults.totalPages || estimatedPages) > 1,
          summary: {
            totalPeople: validationResults.totalResults || estimatedContacts,
            hasEmail: validationResults.people?.filter(p => !p.needs_enrichment).length || 0,
            needsEnrichment: validationResults.people?.filter(p => p.needs_enrichment).length || 0
          }
        };

        // Store results and search params
        setSearchResults(firstPageResult);
        setOriginalSearchParams(scrapingParams);
        setCurrentPageData(firstPageResult.allPeople);
        setTotalCreditsUsed(0);
        setCreditLimit('');
        setEmailLookupResult(null);
        setSelectedContacts([]);
        setSelectAllEmails(false);
        setSelectFirst50(false);

        // Skip people-search and go directly to fetch remaining pages (only if not already fetched)
        let allFetchedContacts;
        if (!allPagesFetched) {
          allFetchedContacts = await fetchAllRemainingPages(firstPageResult, scrapingParams);

          // Track original enrichment status and email for each contact
          allFetchedContacts = allFetchedContacts.map(contact => ({
            ...contact,
            originallyNeeded: contact.needs_enrichment,
            originalEmail: contact.email // Track original LinkedIn email
          }));

          setAllPagesFetched(true);
        } else {
          // Use existing data if all pages were already fetched
          allFetchedContacts = currentPageData || firstPageResult.allPeople || [];
          console.log(`The app is using saved results. Contact count: ${allFetchedContacts.length}.`);

          // ALWAYS reset enrichment data when returning to Step 2 (since user may change email options)
          console.log('The app is resetting the enrichment results.');

          // Reset to the original contact data without enrichment to force re-processing
          allFetchedContacts = allFetchedContacts.map(contact => {
            // Use the tracked original enrichment status
            const originallyNeededEnrichment = contact.hasOwnProperty('originallyNeeded') ?
              contact.originallyNeeded :
              contact.needs_enrichment; // Fallback if not tracked

            // Use tracked original email or current email as fallback
            const originalEmail = contact.hasOwnProperty('originalEmail') ?
              contact.originalEmail :
              (!originallyNeededEnrichment ? contact.email : null);


            return {
              ...contact,
              // Restore original LinkedIn data
              email: originalEmail,
              needs_enrichment: originallyNeededEnrichment,
              // Reset all enrichment-related fields
              enriched: false,
              cached: false,
              guessed: false,
              hunter_verified: false,
              email_status: undefined,
              source: undefined,
              pattern: undefined,
              confidence: undefined,
              sample_count: undefined,
              confidence_score: undefined,
              // Preserve original tracking fields
              originallyNeeded: originallyNeededEnrichment,
              originalEmail: originalEmail
            };
          });
        }

        setCurrentPageData(allFetchedContacts);
        setSearchResults(previous => ({
          ...previous,
          allPeople: allFetchedContacts,
          summary: {
            totalPeople: allFetchedContacts.length,
            needsEnrichment: allFetchedContacts.filter(contact => contact.needs_enrichment).length,
            hasEmail: allFetchedContacts.filter(contact => !contact.needs_enrichment).length
          }
        }));

        console.log('isCancelled-2', isCancelledRef.current);
        // Check for cancellation before proceeding to email enrichment
        if (isCancelledRef.current) {
          console.log("Operation was cancelled, stopping before email enrichment");
          return;
        }

        console.log("selectedEmailOption:", selectedEmailOption);
        // If "Get All Emails Now" is selected, perform bulk-match after fetching all pages
        if (selectedEmailOption === 'allEmails') {
          console.log(`The app is starting email enrichment. Contact count: ${allFetchedContacts.length}.`);
          const enrichedContacts = await performBulkMatch(allFetchedContacts, firstPageResult.jobId);


          // Ensure enrichedContacts is always an array
          const safeEnrichedContacts = Array.isArray(enrichedContacts) ? enrichedContacts : [];

          // Update the current page data with enriched contacts
          setCurrentPageData(safeEnrichedContacts);

          // Update search results with enriched data
          setSearchResults(prev => ({
            ...prev,
            allPeople: safeEnrichedContacts,
            summary: {
              totalPeople: safeEnrichedContacts.length,
              needsEnrichment: safeEnrichedContacts.filter(p => p.needs_enrichment).length,
              hasEmail: safeEnrichedContacts.filter(p => !p.needs_enrichment).length,
              verified: safeEnrichedContacts.filter(p => !p.needs_enrichment && p.email_status !== 'guess' && p.source !== 'guess' && p.email_status !== 'suggested').length,
              guess: safeEnrichedContacts.filter(p => !p.needs_enrichment && (p.email_status === 'guess' || p.source === 'guess')).length,
              suggested: safeEnrichedContacts.filter(p => !p.needs_enrichment && p.email_status === 'suggested').length
            }
          }));
        }

        // Final cancellation check before proceeding to results
        if (isCancelledRef.current) {
          console.log("Operation was cancelled, not proceeding to results step");
          return;
        }

        setCurrentStep(3);

      } else {
        // Legacy flow: call people-search for cases where we don't have validation results
        // Try to resolve domains for any manually typed companies
        await tryResolveMissingDomains();
        console.log('Starting Apollo people search...');
        const scrapingParams = {
          jobTitles: jobTitles.split(',').map(title => title.trim()).filter(title => title),
          targetCompanies: getCompanyDomainsForSubmission(),
          location: location.split(',').map(loc => loc.trim()).filter(loc => loc),
          keywords: keywords.split(',').map(keyword => keyword.trim()).filter(keyword => keyword),
          companySizes: getCompanySizeRanges(),
          industries: selectedIndustries.length === 0 || (selectedIndustries.length === 1 && selectedIndustries[0] === 'All Industries') ? ['All Industries'] : selectedIndustries
        };

        // STEP 1: Call Apollo people search API endpoint (only first page)
        const response = await fetch('/api/apollo/people-search', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(scrapingParams)
        });

        const result = await response.json();

        if (response.ok) {
          // Store results and search params for future page fetching
          setSearchResults(result);
          setOriginalSearchParams(scrapingParams);
          setCurrentPageData(result.allPeople || []);
          setTotalCreditsUsed(result.creditsUsed ?? 0);
          setCreditLimit('');
        } else {
          alert(`Failed to search Apollo: ${result.error}`);
        }
      }
    } catch (error) {
      console.error('Apollo search error:', error);
      alert('Failed to start scraping. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopScraping = async () => {

    try {
      // Call server-side stop endpoint to cancel ongoing operations
      const response = await fetch('/api/apollo/stop-scraping', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const result = await response.json();
      console.log('Server stop response:', result);

      // Check if any scraping processes were actually running
      const wasScrapingActive = isLoading || isFetchingAllPages || isBulkMatching || isValidating;

      // Set cancellation flag
      isCancelledRef.current = true;

      console.log('isCancelled-1', isCancelledRef.current);
      // Reset loading states
      setIsLoading(false);
      setIsFetchingAllPages(false);
      setIsBulkMatching(false);
      setIsValidating(false);
      setIsSavingLeads(false);
      setIsExporting(false);

      // Reset progress
      setFetchProgress({ current: 0, total: 0 });

      // Go back to step 1 (setup) and reset fetched status
      setCurrentStep(1);
      setAllPagesFetched(false);

      // Only alert if scraping was actually running
      if (wasScrapingActive) {
        alert('Scraping process stopped.');
      }
    } catch (error) {
      console.error('Error stopping scraping:', error);

      // Check if any scraping processes were actually running
      const wasScrapingActive = isLoading || isFetchingAllPages || isBulkMatching || isValidating;

      // Still reset UI states even if server call fails
      setIsLoading(false);
      setIsFetchingAllPages(false);
      setIsBulkMatching(false);
      setIsValidating(false);
      setIsSavingLeads(false);
      setIsExporting(false);
      setFetchProgress({ current: 0, total: 0 });
      setCurrentStep(1);
      setAllPagesFetched(false);

      // Only alert if scraping was actually running
      if (wasScrapingActive) {
        alert('Scraping process stopped (UI only - server may still be processing).');
      }
    }
  };


  // Step management functions
  const steps = [
    { number: 1, title: 'Setup', subtitle: 'Enter targets' },
    { number: 2, title: 'Review & Confirm', subtitle: 'Validate data' },
    { number: 3, title: 'Results', subtitle: 'View contacts' }
  ];

  const handleContinueToReview = async () => {
    setIsValidating(true);

    try {
      // Auto-search for company domains before validation
      console.log('Auto-resolving company domains...');
      const resolvedCompanies = await tryResolveMissingDomains();

      if (resolvedCompanies && resolvedCompanies.length > 0) {
        console.log('Auto-resolved companies:', resolvedCompanies);
      }

      // Prepare search parameters for validation
      const scrapingParams = {
        jobTitles: jobTitles.split(',').map(title => title.trim()).filter(title => title),
        targetCompanies: getCompanyDomainsForSubmission(),
        location: location.split(',').map(loc => loc.trim()).filter(loc => loc),
        keywords: keywords.split(',').map(keyword => keyword.trim()).filter(keyword => keyword),
        companySizes: getCompanySizeRanges(),
        industries: selectedIndustries.length === 0 || (selectedIndustries.length === 1 && selectedIndustries[0] === 'All Industries') ? ['All Industries'] : selectedIndustries,
        validateOnly: true // Add flag to indicate this is for validation only
      };

      // Call the API for validation and estimates
      const response = await fetch('/api/apollo/people-search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(scrapingParams)
      });

      const result = await response.json();

      if (response.ok) {
        // Store resolved companies info with validation results
        const enhancedResult = {
          ...result,
          resolvedCompanies: resolvedCompanies || []
        };

        setValidationResults(enhancedResult);
        setEstimatedContacts(result.totalResults || 0); // Use API result, default to 0 if none
        setEstimatedPages(result.totalPages || Math.ceil((result.totalResults || 0) / 25)); // Calculate pages (25 contacts per page)
        setCurrentStep(2);
      } else {
        alert(`Validation failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Validation error:', error);
      // Set default values on error and ask user what to do
      setEstimatedContacts(0);
      setEstimatedPages(0);
      alert('Unable to validate search criteria. Please check your inputs and try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleBackToSetup = () => {
    setCurrentStep(1);
    // Reset fetched status if going back to setup (user might want to change criteria)
    setAllPagesFetched(false);
  };

  // Function to automatically fetch all remaining pages
  const fetchAllRemainingPages = async (initialResult, searchParams) => {
    if (!initialResult.additionalPagesAvailable || initialResult.currentPage >= initialResult.totalPages) {
      return initialResult.allPeople || []; // Return the contacts from the first page
    }

    setIsFetchingAllPages(true);
    setFetchProgress({ current: initialResult.currentPage, total: initialResult.totalPages });

    let allPeople = [...(initialResult.allPeople || [])];
    let currentJobId = initialResult.jobId;
    let currentPage = initialResult.currentPage;
    let totalCredits = initialResult.creditsUsed || 0;

    try {
      // Fetch remaining pages sequentially
      for (let pageNum = currentPage + 1; pageNum <= initialResult.totalPages; pageNum++) {
        // Update progress
        setFetchProgress({ current: pageNum - 1, total: initialResult.totalPages });
        const response = await fetch('/api/apollo/fetch-next-page', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            jobId: currentJobId,
            pageNumber: pageNum,
            searchParams: searchParams
          })
        });

        const result = await response.json();

        if (response.ok) {
          // Add new people to the collection
          allPeople = [...allPeople, ...result.newPeople];
          // Update state with accumulated data
          setCurrentPageData(allPeople);
          setTotalCreditsUsed(totalCredits);

          // Update search results
          setSearchResults(prev => ({
            ...prev,
            currentPage: pageNum,
            allPeople: allPeople,
            summary: {
              totalPeople: allPeople.length,
              needsEnrichment: allPeople.filter(p => p.needs_enrichment).length,
              hasEmail: allPeople.filter(p => !p.needs_enrichment).length
            }
          }));

          console.log(`Fetched page ${pageNum}/${initialResult.totalPages} - Total contacts: ${allPeople.length}`);
        } else {
          console.error(`Failed to fetch page ${pageNum}:`, result.error);
          // Continue fetching other pages even if one fails
        }
      }

      console.log(`Completed fetching all pages. Total contacts: ${allPeople.length}, Credits used: ${totalCredits}`);

    } catch (error) {
      console.error('Error fetching remaining pages:', error);
    } finally {
      setIsFetchingAllPages(false);
      setFetchProgress({ current: 0, total: 0 });
    }

    // Return the final list of all people for further processing
    return allPeople;
  };

  // Function to perform bulk email enrichment for all contacts
  const performBulkMatch = async (allContacts, jobId, options = {}) => {
    if (!allContacts || allContacts.length === 0) return allContacts;

    const {
      showErrors = true,
      includeSuggestions = true,
      acceptEmailGuesses = acceptGuess
    } = options;

    // Filter contacts that need enrichment
    const contactsNeedingEnrichment = allContacts.filter(p => p.needs_enrichment);

    if (contactsNeedingEnrichment.length === 0) {
      console.log('No contacts need enrichment');
      return allContacts; // Return as-is if no enrichment needed
    }

    console.log(`Starting bulk match for ${contactsNeedingEnrichment.length} contacts...`);
    setIsBulkMatching(true);

    let resultContacts = allContacts; // Default to original contacts

    try {
      const response = await fetch('/api/apollo/bulk-match', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          jobId: jobId,
          selectedPeople: allContacts, // Send all contacts, API will handle which ones to enrich
          accept_guess: acceptEmailGuesses,
          include_suggestions: includeSuggestions
        })
      });

      const result = await response.json();
      if (response.ok) {
        // The API now returns enrichedPeople directly
        const enrichedPeople = result.enrichedPeople || [];

        // Calculate actual credits used from cache stats
        const actualCreditsFromAPI = result.cacheStats?.fromAPI || 0;

        // Update the credits counter with the actual API calls made
        setTotalCreditsUsed(prev => prev + actualCreditsFromAPI);

        // Process enriched contacts to ensure needs_enrichment is correctly set
        const processedContacts = enrichedPeople.map(contact => ({
          ...contact,
          needs_enrichment: !contact.email || contact.email === '' || contact.email.includes('email_not_unlocked@')
        }));

        // Use the processed enriched contacts from the API
        resultContacts = processedContacts.length > 0 ? processedContacts : allContacts;
      } else {
        console.error('Bulk match failed:', result.error);
        if (showErrors) {
          alert(`Email enrichment failed: ${result.error}`);
        }
        resultContacts = allContacts; // Use original contacts if bulk match fails
      }
    } catch (error) {
      console.error('Bulk match error:', error);
      if (showErrors) {
        alert('Email enrichment failed. Please try again.');
      }
      resultContacts = allContacts; // Use original contacts if error occurs
    } finally {
      setIsBulkMatching(false);
    }

    return resultContacts;
  };

  // Function to perform Hunter email lookup
  const performHunterLookup = async (contacts, options = {}) => {
    const {
      showErrors = true,
      includeSuggestions = true,
      acceptEmailGuesses = acceptGuess
    } = options;

    try {
      setIsBulkMatching(true);
      console.log(`Starting Hunter lookup for ${contacts.length} contacts`);

      const response = await fetch('/api/apollo/bulk-hunter-email-lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          recipients: contacts,
          accept_guess: acceptEmailGuesses,
          include_suggestions: includeSuggestions
        })
      });
      if (!response.ok) {
        throw new Error(`Hunter lookup failed: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        console.log(`Hunter lookup successful: ${result.summary.successful}/${result.summary.total} contacts found`);
        return result.results.filter(contact => contact.status === 'success');
      } else {
        throw new Error(result.message || 'Hunter lookup failed');
      }
    } catch (error) {
      console.error('Hunter lookup error:', error);
      if (showErrors) {
        alert(`Hunter email lookup failed: ${error.message}`);
      }
      return [];
    } finally {
      setIsBulkMatching(false);
    }
  };

  const getContactKey = (contact) => {
    if (contact.apollo_person_id) return contact.apollo_person_id;
    return `${contact.first_name || ''}|${contact.last_name || ''}|${contact.company || ''}`.toLowerCase();
  };

  const hasCompleteLastName = (contact) => {
    const lastName = contact?.last_name?.trim();
    return Boolean(lastName) && !lastName.includes('*');
  };

  const handleFindSelectedEmails = async () => {
    const contactsToEnrich = selectedContacts.filter(contact => contact.needs_enrichment);
    if (contactsToEnrich.length === 0) {
      setEmailLookupResult({
        type: 'info',
        message: 'Select at least one contact without an email.'
      });
      return;
    }

    const jobId = searchResults?.jobId || validationResults?.jobId;
    if (!jobId) {
      setEmailLookupResult({
        type: 'error',
        message: 'The search job is missing. Start the LinkedIn search again.'
      });
      return;
    }

    setIsFindingSelectedEmails(true);
    setEmailLookupResult(null);

    try {
      const apolloResults = await performBulkMatch(contactsToEnrich, jobId, {
        showErrors: false,
        includeSuggestions: false,
        acceptEmailGuesses: false
      });

      const contactsAfterApollo = apolloResults.map(contact => ({
        ...contact,
        email_provider: contact.needs_enrichment
          ? contact.email_provider
          : contact.cached
            ? 'cache'
            : 'apollo'
      }));
      const apolloFound = contactsAfterApollo.filter(contact => !contact.needs_enrichment).length;

      const contactsForHunter = contactsAfterApollo.filter(contact =>
        contact.needs_enrichment &&
        contact.first_name &&
        hasCompleteLastName(contact) &&
        contact.company
      );

      const hunterResults = contactsForHunter.length > 0
        ? await performHunterLookup(contactsForHunter, {
          showErrors: false,
          includeSuggestions: false,
          acceptEmailGuesses: false
        })
        : [];

      const hunterByContact = new Map(
        hunterResults.map(contact => [getContactKey(contact), contact])
      );
      const enrichedSelectedContacts = contactsAfterApollo.map(contact => {
        const hunterResult = hunterByContact.get(getContactKey(contact));
        if (!hunterResult?.new_email) return contact;

        const reusedProviders = ['contacts', 'scraping_preview', 'cache', 'hunter_cache'];
        const emailProvider = reusedProviders.includes(hunterResult.source)
          ? hunterResult.source
          : 'hunter';

        return {
          ...contact,
          email: hunterResult.new_email,
          needs_enrichment: false,
          enriched: true,
          email_status: hunterResult.email_status || 'verified',
          source: hunterResult.source,
          email_provider: emailProvider,
          hunter_verified: true,
          confidence_score: hunterResult.confidence_score,
          confidence: hunterResult.confidence
        };
      });

      const enrichedByContact = new Map(
        enrichedSelectedContacts.map(contact => [getContactKey(contact), contact])
      );
      const updatedContacts = (currentPageData || []).map(contact =>
        enrichedByContact.get(getContactKey(contact)) || contact
      );

      const selectedKeys = new Set(selectedContacts.map(getContactKey));
      const updatedSelection = updatedContacts.filter(contact => selectedKeys.has(getContactKey(contact)));
      const hunterFound = hunterResults.filter(contact => contact.new_email).length;
      const unresolved = enrichedSelectedContacts.filter(contact => contact.needs_enrichment).length;
      const skipped = contactsAfterApollo.filter(contact =>
        contact.needs_enrichment &&
        (!contact.first_name || !hasCompleteLastName(contact) || !contact.company)
      ).length;
      const hunterCredits = hunterResults.filter(contact => contact.source === 'api').length;

      if (hunterCredits > 0) {
        setTotalCreditsUsed(previous => previous + hunterCredits);
      }

      setCurrentPageData(updatedContacts);
      setSelectedContacts(updatedSelection);
      setSearchResults(previous => ({
        ...previous,
        allPeople: updatedContacts,
        summary: {
          totalPeople: updatedContacts.length,
          needsEnrichment: updatedContacts.filter(contact => contact.needs_enrichment).length,
          hasEmail: updatedContacts.filter(contact => !contact.needs_enrichment).length,
          verified: updatedContacts.filter(contact =>
            !contact.needs_enrichment &&
            contact.email_status !== 'guess' &&
            contact.source !== 'guess' &&
            contact.email_status !== 'suggested'
          ).length,
          guess: updatedContacts.filter(contact =>
            !contact.needs_enrichment &&
            (contact.email_status === 'guess' || contact.source === 'guess')
          ).length,
          suggested: updatedContacts.filter(contact => contact.email_status === 'suggested').length
        }
      }));

      const skippedText = skipped > 0 ? ` ${skipped} were not sent to Hunter because a complete name and company were unavailable.` : '';
      setEmailLookupResult({
        type: unresolved > 0 ? 'info' : 'success',
        message: `Email search complete. Apollo found ${apolloFound}; Hunter found ${hunterFound}; ${unresolved} remain without an email.${skippedText}`
      });
    } catch (error) {
      console.error('Selected email lookup failed:', error);
      setEmailLookupResult({
        type: 'error',
        message: 'The selected email search failed. No contacts were removed from the results.'
      });
    } finally {
      setIsFindingSelectedEmails(false);
    }
  };

  // Function to save selected leads to database
  const handleSaveLeads = async () => {
    if (selectedContacts.length === 0) {
      alert('Please select at least one contact to save.');
      return;
    }

    // Validate required data
    const jobId = searchResults?.jobId || validationResults?.jobId;
    if (!jobId) {
      alert('Missing job ID. Please restart the scraping process.');
      return;
    }

    if (!selectedContacts || selectedContacts.length === 0) {
      alert('No contacts selected to save.');
      return;
    }

    console.log('Saving leads with jobId:', jobId, 'contacts:', selectedContacts.length);
    setIsSavingLeads(true);

    try {
      const response = await fetch('/api/apollo/confirm-leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          selectedEmailOption: selectedEmailOption,
          jobId: searchResults?.jobId || validationResults?.jobId,
          confirmedLeads: selectedContacts,
          searchParams: originalSearchParams,
          accept_guess: acceptGuess
          //templateId: searchResults?.templateId || validationResults?.templateId || 'default'
        })
      });

      const result = await response.json();

      if (response.ok) {
        // Show success message with correct counts from API response
        const actualSaved = result.insertedCount || 0;
        const actualUpdated = result.updatedCount || 0;
        const reusedContacts = result.reusedContactCount || 0;
        const duplicatesSkipped = result.skippedCount || 0;
        const totalRequested = result.totalRequested || selectedContacts.length;
        const totalProcessed = actualSaved + actualUpdated + reusedContacts;

        let message = `Successfully processed ${totalProcessed} leads!`;
        if (actualUpdated > 0 || reusedContacts > 0 || duplicatesSkipped > 0) {
          message += `\n\nSummary:\n- New leads saved: ${actualSaved}\n- Existing previews updated: ${actualUpdated}\n- Existing contacts reused: ${reusedContacts}\n- Duplicates skipped: ${duplicatesSkipped}\n- Total requested: ${totalRequested}`;
        } else {
          message += `\n\nSummary:\n- New leads saved: ${actualSaved} out of ${totalRequested} requested`;
        }

        alert(message);

        // Reset selections
        setSelectedContacts([]);
        setSelectAllEmails(false);
        setSelectFirst50(false);

        // Optionally go back to main screen or show success state
        if (onBack) {
          onBack();
        }
      } else {
        alert(`Failed to save leads: ${result.error || 'Unknown error occurred'}`);
      }
    } catch (error) {
      console.error('Error saving leads:', error);
      alert('Failed to save leads. Please try again.');
    } finally {
      setIsSavingLeads(false);
    }
  };

  // Function to export contacts as CSV
  const handleExportCSV = async () => {
    const contactsToExport = selectedContacts.length > 0 ? selectedContacts : currentPageData || [];

    if (contactsToExport.length === 0) {
      alert('No contacts available to export.');
      return;
    }

    setIsExporting(true);

    try {
      // Create CSV content
      const csvHeaders = [
        'First Name',
        'Last Name',
        'Job Title',
        'Company',
        'Email',
        'LinkedIn URL',
        'Email Status',
        'Industry',
        'Location'
      ];

      const csvRows = contactsToExport.map(person => [
        person.first_name || '',
        person.last_name || '',
        person.job_title || '',
        person.company || '',
        person.needs_enrichment ? '' : (person.email || ''),
        person.linkedin_url || '',
        person.email_status,
        person.industry || '',
        person.location || ''
      ]);

      // Convert to CSV format
      const csvContent = [
        csvHeaders.join(','),
        ...csvRows.map(row =>
          row.map(field =>
            // Escape commas and quotes in CSV fields
            typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))
              ? `"${field.replace(/"/g, '""')}"`
              : field
          ).join(',')
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);

        // Generate filename with timestamp
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const exportType = selectedContacts.length > 0 ? 'selected' : 'all';
        const filename = `linkedin-contacts-${exportType}-${timestamp}.csv`;

        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Show success message
        const exportedCount = contactsToExport.length;
        const exportedType = selectedContacts.length > 0 ? 'selected contacts' : 'all contacts';
        alert(`Successfully exported ${exportedCount} ${exportedType} to CSV file: ${filename}`);
      } else {
        throw new Error('CSV download not supported in this browser');
      }

    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };



  return (
    <div className="max-w-5xl mx-auto space-y-8 p-6">
      {/* Step Indicator */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex-1"></div> {/* Left spacer */}
        <div className="flex items-center space-x-8">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${currentStep === step.number
                  ? 'bg-blue-600 text-white'
                  : currentStep > step.number
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                  }`}>
                  {currentStep > step.number ? <Check size={16} /> : step.number}
                </div>
                <div>
                  <div className={`font-medium ${currentStep === step.number ? 'text-blue-600' : 'text-gray-700'}`}>
                    {step.title}
                  </div>
                  <div className="text-sm text-gray-500">{step.subtitle}</div>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-16 h-0.5 mx-4 ${currentStep > step.number ? 'bg-green-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>
        {/* Exit Button */}
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 ml-12"
        >
          <LogOut size={16} />
          Cancel Scraping
        </button>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">LinkedIn Scraper</h1>
        </div>

        {/* Step 1: Setup */}
        {currentStep === 1 && (
          <div className="px-8 py-6">
            {/* Smart Scraping Info Box */}
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong className="font-medium">Smart Scraping:</strong> Enter your targeting criteria below. We'll validate your input before scraping to ensure accurate results and prevent wasted credits.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Industry */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Industry
                </label>
                <div ref={industryDropdownRef} className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setIsIndustryDropdownOpen(!isIndustryDropdownOpen);
                      if (!isIndustryDropdownOpen) {
                        setIndustrySearchTerm('');
                      }
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between"
                  >
                    <span className="text-gray-700">{getSelectedIndustriesDisplay()}</span>
                    <ChevronDown className="text-gray-400" size={20} />
                  </button>

                  {isIndustryDropdownOpen && (
                    <div className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-lg mt-1 shadow-lg z-50">
                      {/* Search input */}
                      <div className="p-3 border-b border-gray-200">
                        <input
                          type="text"
                          value={industrySearchTerm}
                          onChange={(e) => setIndustrySearchTerm(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const filtered = getFilteredIndustries();
                              if (filtered.length > 0) {
                                handleIndustryToggle(filtered[0]);
                              }
                            } else if (e.key === 'Escape') {
                              setIsIndustryDropdownOpen(false);
                            }
                          }}
                          placeholder="Search industries..."
                          className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      </div>

                      {/* Industry list */}
                      <div className="max-h-48 overflow-y-auto">
                        {getFilteredIndustries().length > 0 ? (
                          getFilteredIndustries().map((industry) => {
                            const isSelected = selectedIndustries.includes(industry);
                            return (
                              <div
                                key={industry}
                                onClick={() => handleIndustryToggle(industry)}
                                className="p-3 cursor-pointer hover:bg-gray-50 flex items-center gap-3 border-b last:border-b-0"
                              >
                                <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${isSelected
                                  ? 'bg-blue-600 border-blue-600'
                                  : 'border-gray-300'
                                  }`}>
                                  {isSelected && <Check className="text-white" size={12} />}
                                </div>
                                <span className="text-gray-900">{industry}</span>
                              </div>
                            );
                          })
                        ) : (
                          <div className="p-3 text-gray-500 text-sm text-center">
                            No industries found matching "{industrySearchTerm}"
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Job Title */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Job Title
                </label>
                <input
                  type="text"
                  value={jobTitles}
                  onChange={(e) => setJobTitles(e.target.value)}
                  placeholder="CEO, CTO, VP Marketing, Director of Sales..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter one or multiple job titles separated by commas
                </p>
              </div>

              {/* Target Companies */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Target Companies
                  <span className="text-gray-500 font-normal ml-1">optional</span>
                </label>
                <div ref={containerRef} className="relative">
                  <textarea
                    value={targetCompanies}
                    onChange={(e) => setTargetCompanies(e.target.value)}
                    placeholder="Google, Microsoft, Apple, Amazon, Facebook, Tesla..."
                    className="w-full h-24 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />

                  {/* Autocomplete dropdown */}
                  {suggestions.length > 0 && (
                    <ul className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-lg mt-1 shadow-lg z-50 max-h-60 overflow-y-auto">
                      {suggestions.map((company, index) => (
                        <li
                          key={company.domain || index}
                          onClick={() => handleCompanySelect(company)}
                          className="p-3 cursor-pointer hover:bg-gray-50 flex items-center gap-3 border-b last:border-b-0"
                        >
                          {company.logo && (
                            <img
                              src={company.logo}
                              alt=""
                              className="w-5 h-5 rounded object-cover flex-shrink-0"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <div className="flex-grow min-w-0">
                            <div className="font-medium text-gray-900 truncate">{company.name}</div>
                            {company.domain && (
                              <div className="text-sm text-gray-500 truncate">{company.domain}</div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Show what will be submitted */}
                  {targetCompanies.trim() && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <div className="text-sm font-medium text-gray-700 mb-2">Will be submitted:</div>
                      <div className="flex flex-wrap gap-2">
                        {targetCompanies.split(',').map(company => company.trim()).filter(company => company).map((companyName, index) => {
                          const domain = companyCache[companyName];
                          return (
                            <span
                              key={index}
                              className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${domain
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                                }`}
                            >
                              {companyName}
                              <span className="text-gray-600">→</span>
                              <span className="font-medium">
                                {domain || companyName}
                              </span>
                              {!domain && (
                                <span className="text-yellow-600 ml-1" title="No domain found, will use company name">⚠</span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Enter company names separated by commas, or leave blank to search all companies
                  </p>
                </div>
              </div>

              {/* Keywords */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Keywords
                  <span className="text-gray-500 font-normal ml-1">optional</span>
                </label>
                <input
                  type="text"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder="software engineer, marketing, AI, blockchain..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Search for specific keywords in person profiles, bios, or experience
                </p>
              </div>

              {/* Company Size */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Company Size
                  <span className="text-gray-500 font-normal ml-1">optional</span>
                </label>
                <div ref={companySizeDropdownRef} className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCompanySizeDropdownOpen(!isCompanySizeDropdownOpen);
                      if (!isCompanySizeDropdownOpen) {
                        setCompanySizeSearchTerm('');
                      }
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between"
                  >
                    <span className="text-gray-700">{getSelectedCompanySizesDisplay()}</span>
                    <ChevronDown className="text-gray-400" size={20} />
                  </button>

                  {isCompanySizeDropdownOpen && (
                    <div className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-lg mt-1 shadow-lg z-50">
                      <div className="p-3 border-b border-gray-200">
                        <input
                          type="text"
                          value={companySizeSearchTerm}
                          onChange={(e) => setCompanySizeSearchTerm(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const filtered = getFilteredCompanySizes();
                              if (filtered.length > 0) {
                                handleCompanySizeToggle(filtered[0]);
                              }
                            } else if (e.key === 'Escape') {
                              setIsCompanySizeDropdownOpen(false);
                            }
                          }}
                          placeholder="Search company sizes..."
                          className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {getFilteredCompanySizes().length > 0 ? (
                          getFilteredCompanySizes().map((size) => {
                            const isSelected = selectedCompanySizes.includes(size);
                            return (
                              <div
                                key={size}
                                onClick={() => handleCompanySizeToggle(size)}
                                className="p-3 cursor-pointer hover:bg-gray-50 flex items-center gap-3 border-b last:border-b-0"
                              >
                                <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${isSelected
                                  ? 'bg-blue-600 border-blue-600'
                                  : 'border-gray-300'
                                  }`}>
                                  {isSelected && <Check className="text-white" size={12} />}
                                </div>
                                <span className="text-gray-900">{size}</span>
                              </div>
                            );
                          })
                        ) : (
                          <div className="p-3 text-gray-500 text-sm text-center">
                            No company sizes found matching "{companySizeSearchTerm}"
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Location
                  <span className="text-gray-500 font-normal ml-1">optional</span>
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="San Francisco, New York, Remote, Los Angeles, Seattle..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter locations separated by commas, or leave blank for all locations
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setJobTitles('');
                  setTargetCompanies('');
                  setKeywords('');
                  setLocation('San Francisco, New York, Remote, Los Angeles, Seattle');
                  setSelectedCompanySizes(['All Sizes']);
                  setSelectedIndustries(['All Industries']);
                }}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Clear Form
              </button>
              <button
                onClick={handleContinueToReview}
                disabled={isValidating}
                className="px-8 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isValidating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Auto-resolving & Validating...
                  </>
                ) : (
                  <>
                    Continue to Review
                    <ChevronRight size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Review & Confirm */}
        {currentStep === 2 && (
          <div className="px-8 py-6 space-y-6">
            <h2 className="text-xl font-bold text-gray-900">Confirm LinkedIn Scrape</h2>

            {/* No Results Warning */}
            {estimatedContacts === 0 && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong className="font-medium">⚠️ No Results Found!</strong> Your search criteria returned 0 contacts. Please go back and adjust your targeting parameters to find matching profiles.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Smart Validation Banner */}
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong className="font-medium">✓ Smart Validation Complete!</strong> We've normalized your input to match LinkedIn's data format. Review the changes below before proceeding.
                  </p>
                </div>
              </div>
            </div>

            {/* Input Validation & Corrections */}
            <div className="border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-gray-600 rounded flex items-center justify-center">
                    <span className="text-white text-xs">📝</span>
                  </div>
                  <h3 className="font-medium text-gray-900">Input Validation & Corrections</h3>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Show message when no specific criteria provided */}
                {!jobTitles.trim() && !targetCompanies.trim() && !keywords.trim() &&
                  (selectedIndustries.length === 0 || (selectedIndustries.length === 1 && selectedIndustries[0] === 'All Industries')) &&
                  (selectedCompanySizes.length === 0 || (selectedCompanySizes.length === 1 && selectedCompanySizes[0] === 'All Sizes')) && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-4">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-blue-600">ℹ</span>
                        <span className="font-medium text-blue-800">Broad Search:</span>
                        <span className="text-blue-700">No specific criteria provided - will search all professionals in specified locations</span>
                      </div>
                    </div>
                  )}

                {/* Industry Validation */}
                {selectedIndustries.length > 0 && selectedIndustries[0] !== 'All Industries' && (
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-2">ORIGINAL INDUSTRY</div>
                      <div className="px-3 py-2 bg-yellow-100 rounded text-sm">
                        <span className="text-orange-600">{selectedIndustries.join(', ')}</span>
                        <div className="text-orange-600 text-xs mt-1">Selected</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        LINKEDIN FORMAT
                        <span className="text-green-600">→</span>
                      </div>
                      <div className="px-3 py-2 bg-green-100 rounded text-sm">
                        <span className="text-green-800">{selectedIndustries.join(', ')}</span>
                        <div className="text-green-600 text-xs mt-1 flex items-center gap-1">
                          ✓ Validated
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Job Title Validation */}
                {jobTitles.trim() && (
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-2">ORIGINAL JOB TITLE</div>
                      <div className="px-3 py-2 bg-yellow-100 rounded text-sm">
                        <span className="text-orange-600">{jobTitles}</span>
                        <div className="text-orange-600 text-xs mt-1">User Input</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        LINKEDIN FORMAT
                        <span className="text-green-600">→</span>
                      </div>
                      <div className="px-3 py-2 bg-green-100 rounded text-sm">
                        <span className="text-green-800">{jobTitles.split(',').map(t => t.trim()).filter(t => t).join(', ')}</span>
                        <div className="text-green-600 text-xs mt-1 flex items-center gap-1">
                          ✓ Normalized ({jobTitles.split(',').filter(t => t.trim()).length} titles)
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Companies Validation */}
                {targetCompanies.trim() && (
                  <div>
                    <div className="grid grid-cols-2 gap-8">
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-2">ORIGINAL COMPANIES</div>
                        <div className="px-3 py-2 bg-yellow-100 rounded text-sm">
                          <span className="text-orange-600">{targetCompanies}</span>
                          <div className="text-orange-600 text-xs mt-1">User Input</div>
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          LINKEDIN FORMAT
                          <span className="text-green-600">→</span>
                        </div>
                        <div className="px-3 py-2 bg-green-100 rounded text-sm">
                          <span className="text-green-800">{getCompanyDomainsForSubmission().join(', ')}</span>
                          <div className="text-green-600 text-xs mt-1 flex items-center gap-1">
                            ✓ Cleaned ({getCompanyDomainsForSubmission().length} companies)
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Auto-resolved Companies Display */}
                    {validationResults?.resolvedCompanies && validationResults.resolvedCompanies.length > 0 && (
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-blue-600">🔍</span>
                          <h4 className="font-medium text-blue-800">Auto-Resolved Company Matches</h4>
                        </div>
                        <div className="space-y-2">
                          {validationResults.resolvedCompanies.map((resolved, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-white border border-blue-100 rounded text-sm">
                              <div className="flex items-center gap-2">
                                <span className="text-gray-600">"{resolved.name}" →</span>
                                <span className="font-medium text-blue-800">{resolved.originalName}</span>
                              </div>
                              <div className="text-xs text-gray-500">
                                {resolved.domain}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="text-xs text-blue-600 mt-2">
                          ✓ These companies were automatically matched to improve search accuracy
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Keywords Validation */}
                {keywords.trim() ? (
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-2">ORIGINAL KEYWORDS</div>
                      <div className="px-3 py-2 bg-yellow-100 rounded text-sm">
                        <span className="text-orange-600">{keywords}</span>
                        <div className="text-orange-600 text-xs mt-1">User Input</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        LINKEDIN FORMAT
                        <span className="text-green-600">→</span>
                      </div>
                      <div className="px-3 py-2 bg-green-100 rounded text-sm">
                        <span className="text-green-800">{keywords.split(',').map(k => k.trim()).filter(k => k).join(', ')}</span>
                        <div className="text-green-600 text-xs mt-1 flex items-center gap-1">
                          ✓ Formatted ({keywords.split(',').filter(k => k.trim()).length} keywords)
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-green-600">✓</span>
                      <span className="font-medium text-green-800">Keywords:</span>
                      <span className="text-green-700">No keywords specified - will search all profiles</span>
                    </div>
                  </div>
                )}

                {/* Company Size Validation */}
                {selectedCompanySizes.length > 0 && selectedCompanySizes[0] !== 'All Sizes' && (
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-blue-600">ℹ</span>
                      <span className="font-medium text-blue-800">Company Size:</span>
                      <span className="text-blue-700">{getSelectedCompanySizesDisplay()}</span>
                    </div>
                  </div>
                )}

                {/* Location Validation */}
                {location.trim() && (
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-blue-600">📍</span>
                      <span className="font-medium text-blue-800">Location:</span>
                      <span className="text-blue-700">{location.split(',').map(l => l.trim()).filter(l => l).join(', ')} ({location.split(',').filter(l => l.trim()).length} locations)</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Scrape Estimate */}
            <div className="border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-gray-600 rounded flex items-center justify-center">
                    <span className="text-white text-xs">📊</span>
                  </div>
                  <h3 className="font-medium text-gray-900">Scrape Estimate</h3>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-3 gap-8 text-center">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Target Companies</div>
                    <div className="text-3xl font-bold text-gray-900">
                      {targetCompanies.trim() ?
                        targetCompanies.split(',').filter(c => c.trim()).length :
                        <span className="text-gray-400">All</span>
                      }
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {targetCompanies.trim() ? 'specific companies' : 'companies'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Job Titles</div>
                    <div className="text-3xl font-bold text-gray-900">
                      {jobTitles.trim() ?
                        jobTitles.split(',').filter(t => t.trim()).length :
                        <span className="text-gray-400">All</span>
                      }
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {jobTitles.trim() ? 'specific titles' : 'job titles'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Est. Contacts</div>
                    <div className="text-3xl font-bold text-blue-600">{estimatedContacts || '...'}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {estimatedContacts ? 'LinkedIn profiles' : 'calculating...'}
                    </div>
                  </div>
                </div>

                {/* Pages info */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Total Pages Available:</span>
                    <span className="text-gray-900">{estimatedPages || '...'} pages</span>
                  </div>
                  <div className="flex justify-between items-center text-sm mt-1">
                    <span className="text-gray-600">Status:</span>
                    <span className="text-green-600">
                      ✓ First page retrieved for validation
                      {allPagesFetched && (
                        <span className="ml-2 text-blue-600">• All pages already fetched</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm mt-1">
                    <span className="text-gray-600">Remaining:</span>
                    <span className="text-gray-900">
                      {allPagesFetched ? (
                        <span className="text-blue-600">0 pages (all pages already fetched)</span>
                      ) : (
                        `${estimatedPages ? (estimatedPages - 1) : '...'} pages (0 Apollo credits to retrieve)`
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Do You Want Emails Section */}
            <div className="border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-gray-600 rounded flex items-center justify-center">
                    <span className="text-white text-xs">📧</span>
                  </div>
                  <h3 className="font-medium text-gray-900">Do You Want Emails?</h3>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Basic Info Now, Decide Later - Recommended */}
                <div
                  className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50"
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="emailOption"
                      id="basicInfo"
                      className="mt-1"
                      checked={selectedEmailOption === 'basicInfo'}
                      onChange={() => setSelectedEmailOption('basicInfo')}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <label htmlFor="basicInfo" className="font-medium text-gray-900">✓ Basic Info Now, Decide Later</label>
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">Recommended</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Get names, job titles, and companies first. Then choose which contacts you want emails for on the results page.
                      </p>
                      <p className="text-sm font-medium text-gray-900 mt-2">
                        Basic contact search: <span className="text-green-600">0 Apollo credits</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Email credits are used later only for contacts you select on the results page. Apollo is tried first, then Hunter.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Basic Info Only */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="emailOption"
                      id="basicOnly"
                      className="mt-1"
                      checked={selectedEmailOption === 'basicOnly'}
                      onChange={() => setSelectedEmailOption('basicOnly')}
                    />
                    <div className="flex-1">
                      <label htmlFor="basicOnly" className="font-medium text-gray-900">📋 Basic Info Only (No New Email Lookup)</label>
                      <p className="text-sm text-gray-600 mt-1">
                        Get basic contact details without a new email lookup. Emails already stored in the local cache can still appear.
                      </p>
                      <p className="text-sm font-medium text-gray-900 mt-2">
                        Total cost: <span className="text-green-600">0 Apollo credits</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Apollo People API Search is free for the first page and all additional pages. No email enrichment runs in this mode.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Get All Emails Now */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="emailOption"
                      id="allEmails"
                      className="mt-1"
                      checked={selectedEmailOption === 'allEmails'}
                      onChange={() => setSelectedEmailOption('allEmails')}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <label htmlFor="allEmails" className="font-medium text-gray-900">📨 Get All Emails Now</label>
                        <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded">A Lot!</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Get everything including emails immediately. Retrieves all pages plus performs email lookup for contacts that need emails.
                      </p>
                      {(() => {
                        // Calculate first page email status for display only
                        const firstPageWithEmails = validationResults?.people ? validationResults.people.filter(p => !p.needs_enrichment).length : 0;
                        const firstPageNeedingEmails = validationResults?.people ? validationResults.people.filter(p => p.needs_enrichment).length : 0;
                        const totalEmailLookups = estimatedContacts || 0;

                        return (
                          <>
                            <p className="text-sm text-gray-700 mt-2 bg-blue-50 p-2 rounded">
                              📊 First page: {firstPageWithEmails} contacts have emails, {firstPageNeedingEmails} need lookup. All {estimatedContacts?.toLocaleString() || '...'} contacts will be processed for email enrichment.
                            </p>
                            <p className="text-sm font-medium text-gray-900 mt-2">
                              Basic contact search: <span className="text-green-600">0 Apollo credits</span>. Up to {totalEmailLookups.toLocaleString()} contacts may require paid email enrichment.
                            </p>
                          </>
                        );
                      })()}
                      <p className="text-xs text-gray-500 mt-1">
                        (Actual cost may be lower due to cached emails in database)
                      </p>
                      {/* Accept Guess Option */}
                      <div className="mt-3 flex items-start gap-2">
                        <input
                          type="checkbox"
                          id="acceptGuessAll"
                          checked={acceptGuess && selectedEmailOption === 'allEmails'}
                          onChange={(e) => setAcceptGuess(e.target.checked)}
                          disabled={selectedEmailOption !== 'allEmails'}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <label htmlFor="acceptGuessAll" className="text-sm font-medium text-gray-700">
                            💡 Enable Smart Email Guessing
                          </label>
                          <p className="text-xs text-gray-600 mt-1">
                            Try AI pattern-based email guessing first before expensive API lookups. Can significantly reduce costs while maintaining accuracy.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Credit Cost Breakdown */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-yellow-600">⚠️</span>
                    <h4 className="font-medium text-yellow-800">Credit Cost Breakdown</h4>
                  </div>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span>First page (validation):</span>
                      <span className="text-green-600"><strong>0 credits</strong></span>
                    </div>
                    <div className="flex justify-between">
                      <span>Additional pages:</span>
                      <span>{estimatedPages ? (estimatedPages - 1) : '...'} pages × 0 credits = <strong>0 credits</strong></span>
                    </div>
                    <div className="flex justify-between">
                      <span>Email lookup:</span>
                      <span className="text-gray-600">
                        {selectedEmailOption === 'basicOnly'
                          ? 'Disabled'
                          : selectedEmailOption === 'basicInfo'
                            ? 'Selected contacts only, on results page'
                            : 'All contacts without cached emails'}
                      </span>
                    </div>
                    <div className="border-t border-yellow-300 pt-1 mt-2 flex justify-between font-medium">
                      <span>Cost before email enrichment:</span>
                      <span className="text-green-700">0 credits</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    Apollo People API Search does not use credits. Email enrichment costs depend on the provider, available cache entries, returned data, and your provider plan.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button
                onClick={handleStopScraping}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <ChevronLeft size={16} />
                Go Back & Edit
              </button>
              <button
                onClick={handleStartScraping}
                disabled={isLoading || isFetchingAllPages || isBulkMatching || estimatedContacts === 0}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                {isLoading || isFetchingAllPages || isBulkMatching ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {isFetchingAllPages ? (
                      `Fetching pages ${fetchProgress.current}/${fetchProgress.total}...`
                    ) : isBulkMatching ? (
                      'Enriching emails...'
                    ) : (
                      'Starting...'
                    )}
                  </>
                ) : estimatedContacts === 0 ? (
                  <>
                    No Results to Scrape
                    <X size={16} />
                  </>
                ) : (
                  <>
                    Confirm & Start Scraping
                    <ChevronRight size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Results */}
        {currentStep === 3 && (
          <div className="px-8 py-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Scrape Results</h2>

            {/* Success Banner */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                  <Check className="text-white" size={16} />
                </div>
                <div>
                  <span className="font-medium text-green-800">
                    Successfully scraped {searchResults?.summary?.totalPeople || currentPageData?.length || 0} contacts • {totalCreditsUsed} email enrichment credits used
                    {selectedEmailOption === 'allEmails' && ' (includes email enrichment, cached contacts excluded)'}
                    {acceptGuess && (
                      <>
                        {' • '}
                        <span className="text-orange-700">
                          {(() => {
                            const guessCount = currentPageData?.filter(p =>
                              !p.needs_enrichment &&
                              (p.email_status === 'guess' || p.source === 'guess')
                            ).length || 0;
                            return `${guessCount} emails generated via AI patterns`;
                          })()}
                        </span>
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-5 gap-4 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {searchResults?.summary?.totalPeople || currentPageData?.length || 0}
                </div>
                <div className="text-sm text-gray-600">Total Contacts</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {(() => {
                    const verifiedEmails = currentPageData?.filter(p =>
                      !p.needs_enrichment &&
                      p.email_status !== 'guess' &&
                      p.source !== 'guess' &&
                      p.email_status !== 'suggested'
                    ).length || 0;
                    return verifiedEmails;
                  })()}
                </div>
                <div className="text-sm text-gray-600">Verified Emails</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {(() => {
                    const guessEmails = currentPageData?.filter(p =>
                      !p.needs_enrichment &&
                      (p.email_status === 'guess' || p.source === 'guess')
                    ).length || 0;
                    return guessEmails;
                  })()}
                </div>
                <div className="text-sm text-gray-600">Email Guesses</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {(() => {
                    const suggestedEmails = currentPageData?.filter(p =>
                      !p.needs_enrichment &&
                      p.email_status === 'suggested'
                    ).length || 0;
                    return suggestedEmails;
                  })()}
                </div>
                <div className="text-sm text-gray-600">Email Suggested</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {searchResults?.summary?.needsEnrichment || currentPageData?.filter(p => p.needs_enrichment).length || 0}
                </div>
                <div className="text-sm text-gray-600">No Emails</div>
              </div>
            </div>

            {/* Selection Controls */}
            <div className="flex flex-col gap-3 mb-4 pb-4 border-b border-gray-200 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="mr-2"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedContacts(currentPageData || []);
                      } else {
                        setSelectedContacts([]);
                      }
                    }}
                    checked={selectedContacts.length === currentPageData?.length && currentPageData?.length > 0}
                  />
                  <span className="text-sm text-gray-700">Select All</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={selectAllEmails}
                    onChange={(e) => {
                      setSelectAllEmails(e.target.checked);
                      if (e.target.checked) {
                        setSelectedContacts(currentPageData?.filter(p => !p.needs_enrichment) || []);
                        setSelectFirst50(false);
                      } else {
                        setSelectedContacts([]);
                      }
                    }}
                  />
                  <span className="text-sm text-gray-700">
                    Select All With Emails ({searchResults?.summary?.hasEmail || currentPageData?.filter(p => !p.needs_enrichment).length || 0})
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={selectFirst50}
                    onChange={(e) => {
                      setSelectFirst50(e.target.checked);
                      if (e.target.checked) {
                        setSelectedContacts((currentPageData || []).slice(0, 50));
                        setSelectAllEmails(false);
                      } else {
                        setSelectedContacts([]);
                      }
                    }}
                  />
                  <span className="text-sm text-red-600">Select First 50</span>
                </label>
                {selectedEmailOption === 'basicInfo' && (
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={
                        (currentPageData?.filter(contact => contact.needs_enrichment).length || 0) > 0 &&
                        currentPageData
                          .filter(contact => contact.needs_enrichment)
                          .every(contact => selectedContacts.some(selected => getContactKey(selected) === getContactKey(contact)))
                      }
                      onChange={(event) => {
                        if (event.target.checked) {
                          setSelectedContacts(currentPageData?.filter(contact => contact.needs_enrichment) || []);
                          setSelectAllEmails(false);
                          setSelectFirst50(false);
                        } else {
                          setSelectedContacts([]);
                        }
                      }}
                    />
                    <span className="text-sm font-medium text-blue-700">
                      Select All Without Emails ({currentPageData?.filter(contact => contact.needs_enrichment).length || 0})
                    </span>
                  </label>
                )}
              </div>
              {selectedEmailOption === 'basicInfo' && (
                <button
                  type="button"
                  onClick={handleFindSelectedEmails}
                  disabled={
                    isFindingSelectedEmails ||
                    isBulkMatching ||
                    selectedContacts.filter(contact => contact.needs_enrichment).length === 0
                  }
                  className="px-5 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {isFindingSelectedEmails || isBulkMatching ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Finding emails...
                    </>
                  ) : (
                    <>
                      <Mail size={16} />
                      Find Emails for Selected ({selectedContacts.filter(contact => contact.needs_enrichment).length})
                    </>
                  )}
                </button>
              )}
            </div>

            {selectedEmailOption === 'basicInfo' && emailLookupResult && (
              <div className={`mb-4 rounded-lg border p-3 text-sm ${emailLookupResult.type === 'error'
                ? 'border-red-200 bg-red-50 text-red-700'
                : emailLookupResult.type === 'success'
                  ? 'border-green-200 bg-green-50 text-green-700'
                  : 'border-blue-200 bg-blue-50 text-blue-700'
                }`}>
                {emailLookupResult.message}
              </div>
            )}

            {/* Results Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name & LinkedIn</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(() => {
                    const allContacts = currentPageData || [];
                    const totalPages = Math.ceil(allContacts.length / resultsPerPage);
                    const startIndex = (resultsCurrentPage - 1) * resultsPerPage;
                    const endIndex = startIndex + resultsPerPage;
                    const currentPageContacts = allContacts.slice(startIndex, endIndex);

                    return currentPageContacts.length > 0 ? (
                      currentPageContacts.map((person, index) => {
                        const isSelected = selectedContacts.some(contact =>
                          getContactKey(contact) === getContactKey(person)
                        );

                        return (
                          <tr key={startIndex + index}>
                            <td className="px-4 py-4">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedContacts(prev => [...prev, person]);
                                  } else {
                                    setSelectedContacts(prev => prev.filter(contact =>
                                      getContactKey(contact) !== getContactKey(person)
                                    ));
                                  }
                                }}
                              />
                            </td>
                            <td className="px-6 py-4">
                              <div className="font-medium text-gray-900">
                                {person.first_name}{' '}
                                {person.last_name?.trim()
                                  ? person.last_name
                                  : person.last_name_obfuscated?.trim()
                                    ? person.last_name_obfuscated
                                    : ''}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">{person.job_title || 'N/A'}</td>
                            <td className="px-6 py-4 text-sm text-gray-900">{person.company || 'N/A'}</td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${person.email_status === 'guess' || person.source === 'guess'
                                ? 'bg-orange-100 text-orange-800'
                                : person.email_status === 'suggested'
                                  ? 'bg-purple-100 text-purple-800'
                                  : person.needs_enrichment
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                {person.email_status === 'guess' || person.source === 'guess' ? (
                                  <>
                                    🔮 Email Guess
                                  </>
                                ) : person.email_status === 'suggested' ? (
                                  <>
                                    💡 Email Suggested
                                  </>
                                ) : person.needs_enrichment ? (
                                  <>
                                    <X size={12} className="mr-1" />
                                    No Email
                                  </>
                                ) : (
                                  '✓ Verified Email'
                                )}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              {(person.email_status === 'guess' || person.source === 'guess') || person.email_status === 'suggested' || !person.needs_enrichment ? (
                                <div className="text-sm">
                                  <div className="text-gray-900">
                                    {person.email || `${person.first_name.toLowerCase()}.${person.last_name.toLowerCase()}@${person.company?.toLowerCase().replace(' ', '') || 'company'}.com`}
                                  </div>
                                  {(person.email_status === 'guess' || person.source === 'guess') && (
                                    <div className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                                      🔮 Generated via pattern: {person.pattern}
                                      {person.confidence && (
                                        <span className="bg-orange-100 text-orange-800 px-1 rounded text-xs">
                                          {Math.round(person.confidence * 100)}% confidence
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {person.email_status === 'suggested' && (
                                    <div className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                                      💡 Suggested via pattern: {person.email_suggestion_metadata?.pattern || 'unknown'}
                                      {person.email_suggestion_metadata?.confidence && (
                                        <span className="bg-purple-100 text-purple-800 px-1 rounded text-xs">
                                          {Math.round(person.email_suggestion_metadata.confidence * 100)}% confidence
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {person.hunter_verified && (
                                    <div className="text-xs text-green-600 mt-1">
                                      ✓ Hunter verified
                                      {person.confidence_score && (
                                        <span className="bg-green-100 text-green-800 px-1 rounded text-xs ml-1">
                                          {Math.round(person.confidence_score)}% score
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {person.email_provider === 'apollo' && (
                                    <div className="text-xs text-blue-600 mt-1">
                                      ✓ Found by Apollo
                                    </div>
                                  )}
                                  {(person.email_provider === 'cache' || person.email_provider === 'hunter_cache') && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      ✓ Found in cache
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400">−</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                          No contacts found
                        </td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>

              {/* Pagination Controls */}
              {(() => {
                const allContacts = currentPageData || [];
                const totalPages = Math.ceil(allContacts.length / resultsPerPage);

                if (totalPages <= 1) return null;

                const getPageNumbers = () => {
                  const delta = 2;
                  const range = [];
                  const rangeWithDots = [];

                  for (let i = Math.max(2, resultsCurrentPage - delta); i <= Math.min(totalPages - 1, resultsCurrentPage + delta); i++) {
                    range.push(i);
                  }

                  if (resultsCurrentPage - delta > 2) {
                    rangeWithDots.push(1, '...');
                  } else {
                    rangeWithDots.push(1);
                  }

                  rangeWithDots.push(...range);

                  if (resultsCurrentPage + delta < totalPages - 1) {
                    rangeWithDots.push('...', totalPages);
                  } else if (totalPages > 1) {
                    rangeWithDots.push(totalPages);
                  }

                  return rangeWithDots.filter((item, index, arr) => arr.indexOf(item) === index);
                };

                return (
                  <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-700">
                        Showing {((resultsCurrentPage - 1) * resultsPerPage) + 1} to {Math.min(resultsCurrentPage * resultsPerPage, allContacts.length)} of {allContacts.length} results
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setResultsCurrentPage(Math.max(1, resultsCurrentPage - 1))}
                          disabled={resultsCurrentPage === 1}
                          className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft size={16} />
                        </button>

                        <div className="flex items-center gap-1">
                          {getPageNumbers().map((page, index) => (
                            <button
                              key={index}
                              onClick={() => typeof page === 'number' ? setResultsCurrentPage(page) : null}
                              disabled={page === '...'}
                              className={`px-3 py-2 text-sm rounded-md ${page === resultsCurrentPage
                                ? 'bg-blue-600 text-white'
                                : page === '...'
                                  ? 'text-gray-400 cursor-default'
                                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                              {page}
                            </button>
                          ))}
                        </div>

                        <button
                          onClick={() => setResultsCurrentPage(Math.min(totalPages, resultsCurrentPage + 1))}
                          disabled={resultsCurrentPage === totalPages}
                          className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Bottom Notice and Actions */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                Select contacts to save to your database. Contacts can be saved with or without emails - you can get emails later.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center">
              <button
                onClick={() => setCurrentStep(2)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <ChevronLeft size={16} />
                Back to Review
              </button>

              <div className="flex gap-3">
                <button
                  onClick={handleExportCSV}
                  disabled={isExporting}
                  className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {isExporting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <span>📤</span>
                      Export CSV
                    </>
                  )}
                </button>
                <button
                  className="px-8 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  disabled={selectedContacts.length === 0 || isSavingLeads || isFindingSelectedEmails || isBulkMatching}
                  onClick={handleSaveLeads}
                >
                  {isSavingLeads ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving {selectedContacts.length} Leads...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      Continue & Save {selectedContacts.length} Leads
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};
